<template>
  <div class="invite-sub background-gradient">
    <div class="header">
      <div class="back" @click="$router.back()">
        <i class="van-icon van-icon-arrow-left"></i>
      </div>
      <h1>{{ $t('Subordinate Details') }}</h1>
      <div class="header-right"></div>
    </div>

    <div class="content">
      <div class="filter-bar">
        <div class="range-picker" v-click-outside="closeRange">
          <button type="button" class="range-trigger" @click="showRange = !showRange">
            <span>{{ rangeLabel }}</span>
            <i class="arrow" :class="{ open: showRange }"></i>
          </button>
          <div class="range-menu" v-show="showRange">
            <div
              v-for="opt in rangeOptions"
              :key="opt.value"
              class="range-option"
              :class="{ active: range === opt.value }"
              @click="pickRange(opt.value)"
            >
              <span>{{ opt.label }}</span>
              <i v-if="range === opt.value" class="check">✓</i>
            </div>
          </div>
        </div>
        <input
          v-model="keyword"
          class="search-input"
          type="text"
          :placeholder="$t('Enter member ID or username')"
          @keyup.enter="reload"
        >
        <button class="search-btn" type="button" @click="reload">{{ $t('Search') }}</button>
      </div>

      <div class="summary-panel">
        <div class="summary-main">
          <div class="summary-item">
            <span class="label">{{ $t('Direct Count') }}</span>
            <span class="value">{{ summary.direct_count || 0 }}</span>
          </div>
          <div class="summary-item">
            <span class="label">{{ $t("Today's Total Turnover") }}</span>
            <span class="value gold">{{ fmt(summary.today_turnover) }}</span>
          </div>
        </div>
        <div class="summary-grid">
          <div class="grid-item">
            <span class="label">{{ $t('Period Recharge') }}</span>
            <span class="value">{{ fmt(summary.recharge) }}</span>
          </div>
          <div class="grid-item">
            <span class="label">{{ $t('Period Turnover') }}</span>
            <span class="value">{{ fmt(summary.turnover) }}</span>
          </div>
          <div class="grid-item">
            <span class="label">{{ $t('Period Win') }}</span>
            <span class="value up">{{ fmt(summary.win_amount) }}</span>
          </div>
          <div class="grid-item">
            <span class="label">{{ $t('Period Loss') }}</span>
            <span class="value down">{{ fmt(summary.loss_amount) }}</span>
          </div>
          <div class="grid-item wide">
            <span class="label">{{ $t('Period Net Win/Loss') }}</span>
            <span class="value" :class="netClass(summary.net_win_loss)">{{ fmt(summary.net_win_loss) }}</span>
          </div>
        </div>
      </div>

      <div class="list-wrap">
        <div v-if="loading" class="empty">{{ $t('Loading') }}...</div>
        <div v-else-if="!list.length" class="empty">{{ $t('No data yet') }}</div>
        <div v-for="item in list" :key="item.id" class="member-card">
          <div class="card-head">
            <div class="user-line">
              <span class="uid">ID {{ item.id }}</span>
              <span class="uname">{{ item.username }}</span>
            </div>
            <div class="reg-time">{{ $t('Registration Time') }}: {{ item.registered_at || '-' }}</div>
          </div>
          <div class="metric-row">
            <div class="metric">
              <span class="m-label">{{ $t('Recharge') }}</span>
              <span class="m-value">{{ fmt(item.recharge) }}</span>
            </div>
            <div class="metric">
              <span class="m-label">{{ $t("Today's Turnover") }}</span>
              <span class="m-value">{{ fmt(item.today_turnover) }}</span>
            </div>
            <div class="metric">
              <span class="m-label">{{ $t('Total Turnover') }}</span>
              <span class="m-value">{{ fmt(item.turnover) }}</span>
            </div>
          </div>
          <div class="metric-row">
            <div class="metric">
              <span class="m-label">{{ $t('Win') }}</span>
              <span class="m-value up">{{ fmt(item.win_amount) }}</span>
            </div>
            <div class="metric">
              <span class="m-label">{{ $t('Loss') }}</span>
              <span class="m-value down">{{ fmt(item.loss_amount) }}</span>
            </div>
            <div class="metric">
              <span class="m-label">{{ $t('Net Win/Loss') }}</span>
              <span class="m-value" :class="netClass(item.net_win_loss)">{{ fmt(item.net_win_loss) }}</span>
            </div>
          </div>
          <div class="balance-line">
            {{ $t('Account Balance') }}: <em>{{ fmt(item.balance) }}</em>
          </div>
        </div>
      </div>

      <div class="pager" v-if="totalPages > 1">
        <button :disabled="page <= 1" @click="changePage(page - 1)">{{ $t('Previous Page') }}</button>
        <span>{{ page }} / {{ totalPages }}</span>
        <button :disabled="page >= totalPages" @click="changePage(page + 1)">{{ $t('Next Page') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'inviteSubordinates',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__clickOutsideHandler = function (e) {
          if (!el.contains(e.target) && typeof binding.value === 'function') {
            binding.value();
          }
        };
        document.addEventListener('click', el.__clickOutsideHandler, true);
      },
      unbind(el) {
        document.removeEventListener('click', el.__clickOutsideHandler, true);
        delete el.__clickOutsideHandler;
      }
    }
  },
  data() {
    return {
      range: '30',
      showRange: false,
      keyword: '',
      page: 1,
      perPage: 10,
      total: 0,
      loading: false,
      list: [],
      summary: {
        direct_count: 0,
        today_turnover: 0,
        recharge: 0,
        turnover: 0,
        win_amount: 0,
        loss_amount: 0,
        net_win_loss: 0
      }
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.perPage));
    },
    rangeOptions() {
      return [
        { value: '7', label: this.$t('Last 7 days') },
        { value: '30', label: this.$t('Last 30 days') },
        { value: 'all', label: this.$t('All time') }
      ];
    },
    rangeLabel() {
      const hit = this.rangeOptions.find(o => o.value === this.range);
      return hit ? hit.label : this.$t('Last 30 days');
    }
  },
  created() {
    this.fetchList();
  },
  methods: {
    closeRange() {
      this.showRange = false;
    },
    pickRange(val) {
      this.range = val;
      this.showRange = false;
      this.reload();
    },
    fmt(n) {
      const v = Number(n || 0);
      return v.toFixed(2);
    },
    netClass(n) {
      const v = Number(n || 0);
      if (v > 0) return 'up';
      if (v < 0) return 'down';
      return '';
    },
    reload() {
      this.page = 1;
      this.fetchList();
    },
    changePage(p) {
      this.page = p;
      this.fetchList();
    },
    fetchList() {
      const that = this;
      that.loading = true;
      this.$apiFun.post('/api/invite-subordinate-stats', {
        range: that.range,
        keyword: that.keyword,
        page: that.page,
        per_page: that.perPage
      }).then(res => {
        that.loading = false;
        if (res.code === 200 && res.data) {
          that.list = res.data.list || [];
          that.total = res.data.total || 0;
          that.summary = res.data.summary || that.summary;
          that.page = res.data.page || that.page;
        }
      }).catch(() => {
        that.loading = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.background-gradient {
  min-height: 100vh;
  background: #0a0a0a;
  color: #e8e8e8;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #161208;
  border-bottom: 1px solid rgba(209, 160, 62, 0.25);

  h1 {
    font-size: 17px;
    color: #f5e6c8;
    margin: 0;
    font-weight: 600;
  }

  .back, .header-right {
    width: 32px;
    color: #f5e6c8;
    font-size: 18px;
  }
}

.content {
  padding: 14px 12px 28px;
}

.filter-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  align-items: center;

  .search-input {
    background: #141414;
    border: 1px solid rgba(209, 160, 62, 0.28);
    border-radius: 8px;
    color: #eee;
    height: 36px;
    padding: 0 10px;
    font-size: 13px;
    flex: 1;
    min-width: 0;
  }

  .search-btn {
    border: none;
    border-radius: 8px;
    padding: 0 14px;
    height: 36px;
    background: linear-gradient(135deg, #fceb99, #d1a03e);
    color: #6b4214;
    font-weight: 700;
    font-size: 13px;
    flex-shrink: 0;
  }
}

.range-picker {
  position: relative;
  flex-shrink: 0;
  width: 118px;

  .range-trigger {
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    padding: 0 10px;
    background: #141414;
    border: 1px solid rgba(209, 160, 62, 0.35);
    border-radius: 8px;
    color: #f5e6c8;
    font-size: 12px;
    line-height: 1.2;

    .arrow {
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 5px solid #d1a03e;
      transition: transform 0.2s;

      &.open {
        transform: rotate(180deg);
      }
    }
  }

  .range-menu {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    min-width: 100%;
    width: max-content;
    background: #1a160f;
    border: 1px solid rgba(209, 160, 62, 0.4);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
    overflow: hidden;
    z-index: 20;
  }

  .range-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 11px 14px;
    font-size: 13px;
    color: #ddd;
    white-space: nowrap;
    cursor: pointer;

    .check {
      color: #d1a03e;
      font-style: normal;
      font-size: 14px;
    }

    &.active {
      color: #fceb99;
      background: rgba(209, 160, 62, 0.15);
    }

    &:active {
      background: rgba(209, 160, 62, 0.22);
    }
  }
}

.summary-panel {
  background: #111;
  border: 1px solid rgba(209, 160, 62, 0.2);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
}

.summary-main {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.summary-item {
  .label {
    display: block;
    font-size: 12px;
    color: #999;
    margin-bottom: 6px;
  }
  .value {
    font-size: 20px;
    font-weight: 700;
    color: #fff;
  }
  .gold {
    color: #fceb99;
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  .grid-item {
    background: #0c0c0c;
    border-radius: 8px;
    padding: 10px;

    .label {
      display: block;
      font-size: 11px;
      color: #888;
      margin-bottom: 4px;
    }
    .value {
      font-size: 14px;
      font-weight: 600;
    }
  }

  .wide {
    grid-column: 1 / -1;
  }
}

.member-card {
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
}

.card-head {
  margin-bottom: 10px;

  .user-line {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 4px;
  }

  .uid {
    color: #fceb99;
    font-weight: 700;
    font-size: 14px;
  }

  .uname {
    color: #ddd;
    font-size: 14px;
  }

  .reg-time {
    font-size: 12px;
    color: #888;
  }
}

.metric-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-bottom: 8px;
}

.metric {
  background: #0c0c0c;
  border-radius: 8px;
  padding: 8px 6px;
  text-align: center;

  .m-label {
    display: block;
    font-size: 11px;
    color: #888;
    margin-bottom: 4px;
  }

  .m-value {
    font-size: 13px;
    font-weight: 600;
    color: #eee;
  }
}

.balance-line {
  font-size: 12px;
  color: #999;
  padding-top: 4px;

  em {
    font-style: normal;
    color: #fceb99;
    font-weight: 600;
  }
}

.up { color: #5dce8a !important; }
.down { color: #e06767 !important; }

.empty {
  text-align: center;
  color: #777;
  padding: 40px 0;
  font-size: 13px;
}

.pager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 12px;

  button {
    background: #1a1a1a;
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #ddd;
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 12px;

    &:disabled {
      opacity: 0.4;
    }
  }

  span {
    font-size: 12px;
    color: #aaa;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/inviteSubordinates.vue